// src/components/shared/StockShapePicker.tsx
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { STOCK_SHAPES, SHAPE_ORDER, type ShapeId, type StockShapeDef } from '../../lib/stock-shapes'

const HOVER_DELAY_MS = 1000

interface TileProps {
  def: StockShapeDef
  isSelected: boolean
  isSuggested: boolean
  isBest: boolean
  rationale?: string
  onClick(): void
}

function ShapeTile({ def, isSelected, isSuggested, isBest, rationale, onClick }: TileProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const tileRef = useRef<HTMLButtonElement>(null)
  const timerRef = useRef<number | null>(null)

  const computeCoords = () => {
    const el = tileRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setCoords({ top: r.top + r.height / 2, left: r.right + 16 })
  }

  const startHover = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      computeCoords()
      setOpen(true)
    }, HOVER_DELAY_MS)
  }
  const cancelHover = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setOpen(false)
  }

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
  }, [])

  return (
    <>
      <button
        ref={tileRef}
        type="button"
        onClick={onClick}
        onMouseEnter={startHover}
        onMouseLeave={cancelHover}
        onFocus={startHover}
        onBlur={cancelHover}
        className={[
          'group relative flex flex-col items-center justify-center gap-1.5 p-3',
          'border bg-white/[0.02] rounded-sm transition-colors',
          isSelected
            ? 'border-cyan-500 bg-cyan-500/5'
            : 'border-slate-800 hover:border-slate-600',
          isBest && !isSelected ? 'border-cyan-500/40 animate-pulse' : '',
        ].join(' ')}
        data-shape-id={def.id}
      >
        {isSuggested && (
          <span className="absolute top-1 right-1 font-mono text-[8px] tracking-widest text-cyan-400/80 uppercase">
            Sug
          </span>
        )}
        <div className="w-8 h-8 flex items-center justify-center">
          {def.renderSvg('glyph')}
        </div>
        <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500 group-hover:text-slate-300 text-center leading-tight">
          {def.label}
        </span>
      </button>

      {open && coords && createPortal(
        <div
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translateY(-50%)',
            zIndex: 9999,
          }}
          className="w-72 p-4 rounded-sm border border-slate-700 bg-slate-950/95 backdrop-blur-sm shadow-xl pointer-events-none"
        >
          {/* Caret pointing left at the tile */}
          <div
            className="absolute top-1/2 -left-1.5 w-3 h-3 rotate-45 -translate-y-1/2 border-l border-b border-slate-700 bg-slate-950"
          />
          <div className="flex items-start gap-3 relative">
            <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
              {def.renderSvg('card')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-1">
                {def.label}
              </div>
              <p className="font-mono text-xs text-slate-300 leading-relaxed">
                {def.description}
              </p>
              {rationale && (
                <p className="mt-2 pt-2 border-t border-slate-800 font-mono text-[11px] text-cyan-300/80 leading-relaxed">
                  <span className="text-cyan-500">AI:</span> {rationale}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

interface Props {
  value: ShapeId
  onChange(id: ShapeId): void
  suggestedIds?: ShapeId[]
  bestFitId?: ShapeId
  rationales?: Partial<Record<ShapeId, string>>
}

export default function StockShapePicker({
  value, onChange, suggestedIds = [], bestFitId, rationales = {},
}: Props) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {SHAPE_ORDER.map((id) => (
        <ShapeTile
          key={id}
          def={STOCK_SHAPES[id]}
          isSelected={value === id}
          isSuggested={suggestedIds.includes(id)}
          isBest={bestFitId === id}
          rationale={rationales[id]}
          onClick={() => onChange(id)}
        />
      ))}
    </div>
  )
}
